import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check, Shield, Eye, BarChart3 } from 'lucide-react';

interface CookieConsentProps {
  onAccept?: (preferences: CookiePreferences) => void;
  onDecline?: () => void;
}

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export default function CookieConsent({ onAccept, onDecline }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookie-consent');
    if (!consentGiven) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    setIsVisible(false);
    onAccept?.(allAccepted);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    setIsVisible(false);
    onAccept?.(preferences);
  };

  const handleDeclineAll = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    localStorage.setItem('cookie-consent', JSON.stringify(necessaryOnly));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    
    setIsVisible(false);
    onDecline?.();
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(100%); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-slide-up {
          animation: slideUp 0.5s ease-out forwards;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .glass-morphism {
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .premium-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .toggle-switch {
          transition: all 0.3s ease;
        }
        
        .toggle-switch.enabled {
          background: linear-gradient(135deg, #ec4899, #8b5cf6);
        }
        
        .toggle-switch.disabled {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .toggle-dot {
          transition: all 0.3s ease;
        }
        
        .toggle-dot.enabled {
          transform: translateX(24px);
        }
      `}</style>

      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] animate-fade-in" />

      {/* Cookie Consent Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 animate-slide-up">
        <div className="max-w-6xl mx-auto">
          <div className="glass-morphism rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10">
            {!showDetails ? (
              /* Simple View */
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-8">
                {/* Icon and Content */}
                <div className="flex items-start space-x-4 sm:space-x-6 flex-1">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Cookie className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-poppins font-bold text-xl sm:text-2xl lg:text-3xl text-white mb-3 sm:mb-4">
                      We Value Your Privacy
                    </h3>
                    <p className="font-inter text-slate-300 text-sm sm:text-base lg:text-lg leading-relaxed">
                      We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                      By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or learn more in our 
                      <button className="text-pink-400 hover:text-pink-300 underline ml-1 transition-colors">
                        Privacy Policy
                      </button>.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full lg:w-auto">
                  <button
                    onClick={() => setShowDetails(true)}
                    className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/15 transition-all duration-300 text-sm sm:text-base"
                  >
                    <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>Customize</span>
                  </button>
                  
                  <button
                    onClick={handleDeclineAll}
                    className="px-4 sm:px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/15 transition-all duration-300 text-sm sm:text-base"
                  >
                    Decline All
                  </button>
                  
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 sm:px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              /* Detailed View */
              <div>
                {/* Header */}
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Cookie className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                    </div>
                    <h3 className="font-poppins font-bold text-xl sm:text-2xl lg:text-3xl text-white">
                      Cookie Preferences
                    </h3>
                  </div>
                  
                  <button
                    onClick={() => setShowDetails(false)}
                    className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  >
                    <X className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>

                {/* Cookie Categories */}
                <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                  {[
                    {
                      key: 'necessary' as keyof CookiePreferences,
                      icon: Shield,
                      title: 'Necessary Cookies',
                      description: 'Essential for the website to function properly. These cannot be disabled.',
                      required: true,
                      color: 'from-green-500 to-emerald-500'
                    },
                    {
                      key: 'analytics' as keyof CookiePreferences,
                      icon: BarChart3,
                      title: 'Analytics Cookies',
                      description: 'Help us understand how visitors interact with our website by collecting anonymous information.',
                      required: false,
                      color: 'from-blue-500 to-cyan-500'
                    },
                    {
                      key: 'marketing' as keyof CookiePreferences,
                      icon: Eye,
                      title: 'Marketing Cookies',
                      description: 'Used to track visitors across websites to display relevant advertisements.',
                      required: false,
                      color: 'from-purple-500 to-pink-500'
                    },
                    {
                      key: 'preferences' as keyof CookiePreferences,
                      icon: Settings,
                      title: 'Preference Cookies',
                      description: 'Remember your choices and provide enhanced, personalized features.',
                      required: false,
                      color: 'from-orange-500 to-red-500'
                    }
                  ].map((category) => (
                    <div key={category.key} className="premium-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <category.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-poppins font-semibold text-white text-base sm:text-lg">
                                {category.title}
                              </h4>
                              {category.required && (
                                <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-semibold">
                                  Required
                                </span>
                              )}
                            </div>
                            <p className="font-inter text-slate-400 text-xs sm:text-sm leading-relaxed">
                              {category.description}
                            </p>
                          </div>
                        </div>

                        {/* Toggle Switch */}
                        <button
                          onClick={() => togglePreference(category.key)}
                          disabled={category.required}
                          className={`toggle-switch w-12 h-6 sm:w-14 sm:h-7 rounded-full p-1 flex items-center ${
                            preferences[category.key] ? 'enabled' : 'disabled'
                          } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <div className={`toggle-dot w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full ${
                            preferences[category.key] ? 'enabled' : ''
                          }`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
                  <button
                    onClick={handleDeclineAll}
                    className="px-4 sm:px-6 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/15 transition-all duration-300 text-sm sm:text-base"
                  >
                    Decline All
                  </button>
                  
                  <button
                    onClick={handleAcceptSelected}
                    className="px-6 sm:px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 flex items-center space-x-2 text-sm sm:text-base"
                  >
                    <Check className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>Save Preferences</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}